(() => {
  if (window.CBFreeShippingUnlock) return;

  const meterSelector = '[data-free-shipping-meter][data-free-shipping-threshold]';
  const storageKey = 'cb-free-shipping-qualified';

  class FreeShippingUnlockCoordinator {
    constructor() {
      this.qualified = null;
      this.threshold = null;
      this.started = false;
      this.celebrationVersion = 0;
      this.fetchTimer = null;
      this.fetchController = null;
      this.onCartUpdated = this.onCartUpdated.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
      this.onMutations = this.onMutations.bind(this);
    }

    start() {
      if (this.started) return;
      this.started = true;

      document.addEventListener('cb:cart-updated', this.onCartUpdated);
      window.addEventListener('pageshow', this.onPageShow);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdated);
      }

      this.observer = new MutationObserver(this.onMutations);
      this.observer.observe(document.body, { childList: true, subtree: true });

      const initialMeter = document.querySelector(meterSelector);
      if (initialMeter) {
        this.processMeter(initialMeter);
      }
      this.fetchCartState(0);
    }

    onCartUpdated(event) {
      const total = Number(event?.cartData?.total_price ?? event?.detail?.cartData?.total_price);
      if (Number.isFinite(total)) {
        this.processTotal(total, this.getThreshold());
      } else {
        this.fetchCartState(80);
      }
    }

    onPageShow() {
      this.fetchCartState(0);
    }

    onMutations(mutations) {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          const meter = node.matches(meterSelector) ? node : node.querySelector(meterSelector);
          if (!meter) continue;
          this.processMeter(meter);
          return;
        }
      }
    }

    processMeter(meter) {
      const total = Number(meter.dataset.freeShippingTotal);
      const threshold = Number(meter.dataset.freeShippingThreshold);
      if (!Number.isFinite(total) || !Number.isFinite(threshold)) return;
      this.processTotal(total, threshold);
    }

    processTotal(total, threshold) {
      if (!Number.isFinite(threshold) || threshold <= 0) return;

      this.syncMeters(total);

      if (this.threshold !== null && this.threshold !== threshold) {
        this.qualified = null;
      }

      this.threshold = threshold;
      const isQualified = total >= threshold;
      const previous = this.qualified ?? this.readStoredState(threshold);

      if (previous === null) {
        this.qualified = isQualified;
        this.writeStoredState(isQualified, threshold);
        return;
      }

      this.qualified = isQualified;
      this.writeStoredState(isQualified, threshold);

      if (previous && !isQualified) {
        this.celebrationVersion += 1;
        this.clearCelebrations();
        return;
      }

      if (!previous && isQualified) {
        this.celebrationVersion += 1;
        this.celebrateWhenReady(this.celebrationVersion);
      }
    }

    syncMeters(total) {
      document.querySelectorAll(meterSelector).forEach((meter) => {
        const threshold = Number(meter.dataset.freeShippingThreshold);
        if (!Number.isFinite(threshold) || threshold <= 0) return;

        const remaining = Math.max(threshold - total, 0);
        const progress = Math.min((total / threshold) * 100, 100);
        const label = meter.querySelector('[data-free-shipping-label]');
        const totalLabel = meter.querySelector('[data-free-shipping-cart-total]');
        const progressBar = meter.querySelector('[data-free-shipping-progress]');
        const remainingText = meter.dataset.freeShippingRemainingText || meter.dataset.remainingText;
        const unlockedText = meter.dataset.freeShippingUnlockedText || meter.dataset.unlockedText;

        meter.dataset.freeShippingTotal = String(total);
        if (meter.dataset.freeShippingPreviewActive === 'true') return;

        meter.classList.toggle('is-free-shipping-qualified', remaining <= 0);

        if (label && remainingText && unlockedText) {
          label.textContent = remaining > 0
            ? remainingText.replace('[amount]', this.formatMoney(remaining, meter))
            : unlockedText;
        }
        if (totalLabel) totalLabel.textContent = this.formatMoney(total, meter);
        if (progressBar) progressBar.style.width = `${progress}%`;
      });
    }

    formatMoney(cents, meter) {
      const currency = meter.dataset.freeShippingCurrency || window.Shopify?.currency?.active || 'USD';
      const locale = document.documentElement.lang || 'en-US';

      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
      } catch (error) {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
      }
    }

    getThreshold() {
      const meter = document.querySelector(meterSelector);
      const threshold = Number(meter?.dataset.freeShippingThreshold);
      return Number.isFinite(threshold) && threshold > 0 ? threshold : 10000;
    }

    fetchCartState(delay) {
      window.clearTimeout(this.fetchTimer);
      this.fetchTimer = window.setTimeout(async () => {
        this.fetchController?.abort();
        this.fetchController = new AbortController();

        try {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${root}cart.js`, {
            cache: 'no-store',
            signal: this.fetchController.signal,
          });
          if (!response.ok) return;
          const cart = await response.json();
          this.processTotal(Number(cart.total_price) || 0, this.getThreshold());
        } catch (error) {
          if (error.name !== 'AbortError') return;
        }
      }, delay);
    }

    readStoredState(threshold) {
      try {
        const stored = JSON.parse(sessionStorage.getItem(storageKey));
        if (!stored || Number(stored.threshold) !== threshold) return null;
        return Boolean(stored.qualified);
      } catch (error) {
        return null;
      }
    }

    writeStoredState(qualified, threshold) {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify({ qualified, threshold }));
      } catch (error) {
        return;
      }
    }

    celebrateWhenReady(version, attempt = 0) {
      window.setTimeout(() => {
        if (version !== this.celebrationVersion || !this.qualified) return;

        const target = this.selectBestMeter();
        if (!target && attempt < 10) {
          this.celebrateWhenReady(version, attempt + 1);
          return;
        }

        if (target) this.celebrate(target);
      }, attempt === 0 ? 280 : 100);
    }

    selectBestMeter() {
      const meters = Array.from(document.querySelectorAll(meterSelector));
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      return meters
        .map((meter) => {
          const style = window.getComputedStyle(meter);
          const rect = meter.getBoundingClientRect();
          if (
            style.display === 'none' ||
            style.visibility === 'hidden' ||
            Number(style.opacity) === 0 ||
            rect.width <= 0 ||
            rect.height <= 0
          ) {
            return null;
          }

          const visibleWidth = Math.max(0, Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0));
          const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
          const visibleRatio = (visibleWidth * visibleHeight) / (rect.width * rect.height);
          if (visibleRatio <= 0) return null;

          const centerX = rect.left + rect.width / 2;
          const centerY = rect.top + rect.height / 2;
          const centerDistance = Math.hypot(centerX - viewportWidth / 2, centerY - viewportHeight / 2);
          const drawer = meter.closest('cart-drawer');
          const drawerScore = drawer?.classList.contains('active') ? 10000 : 0;
          const fullyVisibleScore = visibleRatio > 0.98 ? 500 : 0;
          const focusScore = meter.contains(document.activeElement) ? 250 : 0;

          return {
            meter,
            score: drawerScore + fullyVisibleScore + focusScore + visibleRatio * 1000 - centerDistance / 10,
          };
        })
        .filter(Boolean)
        .sort((a, b) => b.score - a.score)[0]?.meter;
    }

    celebrate(target) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.clearCelebrations();
      const effect = document.createElement('span');
      effect.className = 'free-shipping-unlock-effect';
      effect.setAttribute('aria-hidden', 'true');
      effect.innerHTML = `
        <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
          <path d="m6.5 12.5 3.4 3.4 7.6-8" />
        </svg>
        <span class="free-shipping-unlock-effect__spark" style="--spark-angle: 0deg"></span>
        <span class="free-shipping-unlock-effect__spark" style="--spark-angle: 45deg"></span>
        <span class="free-shipping-unlock-effect__spark" style="--spark-angle: 90deg"></span>
        <span class="free-shipping-unlock-effect__spark" style="--spark-angle: 135deg"></span>
        <span class="free-shipping-unlock-effect__spark" style="--spark-angle: 180deg"></span>
        <span class="free-shipping-unlock-effect__spark" style="--spark-angle: 225deg"></span>
        <span class="free-shipping-unlock-effect__spark" style="--spark-angle: 270deg"></span>
        <span class="free-shipping-unlock-effect__spark" style="--spark-angle: 315deg"></span>
      `;

      if (target.closest('cart-drawer')) {
        const targetRect = target.getBoundingClientRect();
        effect.classList.add('free-shipping-unlock-effect--viewport');
        effect.style.left = `${targetRect.left + targetRect.width / 2}px`;
        effect.style.top = `${targetRect.top + targetRect.height / 2}px`;
        document.body.appendChild(effect);
      } else {
        target.appendChild(effect);
      }
      void target.offsetWidth;
      target.classList.add('is-free-shipping-unlocked');

      window.setTimeout(() => {
        target.classList.remove('is-free-shipping-unlocked');
        effect.remove();
      }, 1800);
    }

    clearCelebrations() {
      document.querySelectorAll('.is-free-shipping-unlocked').forEach((meter) => {
        meter.classList.remove('is-free-shipping-unlocked');
      });
      document.querySelectorAll('.free-shipping-unlock-effect').forEach((effect) => effect.remove());
    }
  }

  const coordinator = new FreeShippingUnlockCoordinator();
  window.CBFreeShippingUnlock = coordinator;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => coordinator.start(), { once: true });
  } else {
    coordinator.start();
  }
})();
